import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Adjunta el header X-Module-Context en todas las peticiones HTTP que tengan al menos
 * dos segmentos en el path del router activo.
 *
 * El header transporta la URL del grupo de navegación activo (ej: /admin-sistema/roles),
 * que el backend usa para identificar el contexto exacto desde el cual opera el usuario
 * y aplicar el registro de Permission correspondiente a ese grupo.
 *
 * Esto permite que los grupos sean verdaderamente independientes:
 *   - Usuario en /admin-sistema/roles con canView=false → export bloqueado.
 *   - Usuario en /admin/roles con canView=true → export permitido.
 *   - Módulo Usuarios llamando GET /api/roles para un select → su contexto es
 *     /admin/usuarios, que no está en los moduleUrls de roles → el backend usa
 *     anyMatch como fallback y permite la llamada cruzada correctamente.
 *
 * Si el path activo tiene menos de 2 segmentos no se adjunta el header y el backend
 * usa la validación fallback (anyMatch), apropiado para llamadas no contextuales.
 */
export const moduleContextInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes('/api/storefront')) {
    return next(req);
  }

  const router = inject(Router);

  const moduleUrl = extractModuleUrl(router.url);
  if (!moduleUrl) {
    return next(req);
  }

  return next(req.clone({ setHeaders: { 'X-Module-Context': moduleUrl } }));
};

/**
 * Extrae los dos primeros segmentos del path de la URL del router.
 * Ejemplos:
 *   /admin-sistema/roles              → /admin-sistema/roles
 *   /admin/roles?page=2               → /admin/roles
 *   /admin-sistema/permisos/editar/5  → /admin-sistema/permisos
 *   /maestros-sistema/monedas         → /maestros-sistema/monedas
 */
function extractModuleUrl(routerUrl: string): string {
  const path = routerUrl.split('?')[0].split('#')[0];
  const parts = path.split('/').filter(s => s.length > 0);
  if (parts.length < 2) return '';
  return '/' + parts.slice(0, 2).join('/');
}

